import { useState } from 'react';
import type { ReactNode } from 'react';

import type { QuestionAnswer } from '@api/instant_survey/utilities/types';
import { QUESTION_TYPES, QUESTION_TYPES_CAN_CARRY_FORWARD } from '@api/instant_survey';
import { getQuestionTypeIcon } from '@helpers';
import {
  ActionOverlayPopover,
  DragDropV2Icon,
  ExclusiveIcon,
  EyeClosedIcon,
  EyeIcon,
  Layout,
  PinIcon,
  TrashIcon,
} from '@utilities';

import styles from './_index.module.scss';

interface AnswerProps {
  answer: QuestionAnswer;
  children?: ReactNode;
  isDraggable?: boolean;
  onAnchor?: () => void;
  onDelete?: () => void;
  onExcludeFromCarryForward?: () => void;
  onMakeExclusive?: () => void;
  type: string;
}

const Answer = ({
  answer,
  children,
  isDraggable,
  onAnchor = () => {},
  onDelete = () => {},
  onExcludeFromCarryForward = () => {},
  onMakeExclusive = () => {},
  type,
}: AnswerProps) => {
  const [isDropdownActive, setIsDropdownActive] = useState(false);

  const isCheckboxType = [QUESTION_TYPES.CHECKBOX.value, QUESTION_TYPES.TURF.value].includes(type);
  const isMaxDiffType = type === QUESTION_TYPES.MAX_DIFF.value;

  const hasPropertyIcons =
    (answer.isExclusive && isCheckboxType) ||
    (answer.isAnchored && !isMaxDiffType) ||
    (answer.excludeFromCarryForward && QUESTION_TYPES_CAN_CARRY_FORWARD.includes(type));

  return (
    <div className={styles['answer']}>
      <Layout.Flex className={styles['answer-icons']} gap="small">
        {<DragDropV2Icon style={{ visibility: isDraggable ? 'visible' : 'hidden' }} />}
        {getQuestionTypeIcon(isCheckboxType ? QUESTION_TYPES.CHECKBOX.value : type)}
      </Layout.Flex>

      <Layout.Fill className={styles['answer-input-wrapper']}>{children}</Layout.Fill>

      {hasPropertyIcons && (
        <Layout.Flex className={styles['answer-properties']} gap="small">
          {answer.isExclusive && isCheckboxType && <ExclusiveIcon />}
          {answer.isAnchored && !isMaxDiffType && <PinIcon />}
          {answer.excludeFromCarryForward && <EyeClosedIcon />}
          <div className={styles['answer-pipe']} />
        </Layout.Flex>
      )}

      <div className={styles['answer-controls']}>
        <ActionOverlayPopover
          hasArrow={false}
          isActive={isDropdownActive}
          onClick={() => setIsDropdownActive(!isDropdownActive)}
          onHide={() => setIsDropdownActive(false)}
        >
          <ul>
            {!answer.isNoneOfTheAbove && isCheckboxType && (
              <li>
                <button
                  aria-label={
                    answer.isExclusive ? 'Make answer exclusive' : 'Make answer unexclusive'
                  }
                  className="answer-option-button"
                  onClick={() => {
                    onMakeExclusive();
                    setIsDropdownActive(false);
                  }}
                >
                  <ExclusiveIcon />
                  <span>Make {answer.isExclusive ? 'Unexclusive' : 'Exclusive'}</span>
                </button>
              </li>
            )}
            {!answer.isNoneOfTheAbove && !isMaxDiffType && (
              <li>
                <button
                  aria-label={answer.isAnchored ? 'Unpin answer' : 'Pin answer'}
                  onClick={() => {
                    onAnchor();
                    setIsDropdownActive(false);
                  }}
                >
                  <PinIcon />
                  <span>{answer.isAnchored ? 'Unpin' : 'Pin'} Position</span>
                </button>
              </li>
            )}
            {answer.isCarryForwardGenerated && (
              <li>
                <button
                  aria-label={answer.excludeFromCarryForward ? 'Include Option' : 'Exclude Option'}
                  onClick={() => {
                    onExcludeFromCarryForward();
                    setIsDropdownActive(false);
                  }}
                >
                  {answer.excludeFromCarryForward ? <EyeIcon /> : <EyeClosedIcon />}
                  <span>
                    {answer.excludeFromCarryForward ? 'Include Option' : 'Exclude Option'}
                  </span>
                </button>
              </li>
            )}
            {!answer.isCarryForwardGenerated &&
              !answer.isNoneOfTheAbove &&
              (isCheckboxType || !isMaxDiffType) && <hr />}
            {!answer.isCarryForwardGenerated && (
              <li>
                <button
                  aria-label="Delete answer"
                  onClick={() => {
                    onDelete();
                    setIsDropdownActive(false);
                  }}
                >
                  <TrashIcon />
                  <span>Delete</span>
                </button>
              </li>
            )}
          </ul>
        </ActionOverlayPopover>
      </div>
    </div>
  );
};

export default Answer;
